import { useEffect } from 'react';
import type { PressEvent } from 'react-aria';
import { enableSuggestInvites as enableSuggestInvitesContext } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { ButtonProps as LaunchpadButtonProps, LinkButtonProps, TooltipProps } from '@launchpad-ui/components';
import { Button, Link, LinkButton, Perceivable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { useInviteMembersModal } from '#hooks/useInviteMembersModal';
import { useInviteMembersOption } from '#hooks/useInviteMembersOption';
import { useSuggestInviteMembersModal } from '#hooks/useSuggestInviteMembersModal';
import { trackAddMembersList } from '#utils/accounts/accountAnalyticEvents';

import { RequestSeatsControl } from '../requestSeats/RequestSeatsControl';

import type { InviteMembersModalProps } from './InviteMembersModal';

type OwnButtonProps = {
  cta?: string;
  tooltipContent?: TooltipProps['children'];
  isLink?: boolean;
};

type ButtonProps = OwnButtonProps & (Omit<LaunchpadButtonProps, 'ref'> & Omit<LinkButtonProps, 'ref'>);

type AnalyticsProps = {
  componentName: string;
  type: 'link' | 'button';
};

type OwnModalProps = {
  additionalShowModalCheck?: boolean;
  showModalOnRender?: boolean;
};

type ModalProps = OwnModalProps & InviteMembersModalProps;

export type InviteMembersControlProps = {
  buttonProps?: ButtonProps;
  analyticsProps: AnalyticsProps;
  modalProps: ModalProps;
};

// A single source of truth that serves as the control for opening the InviteMembersModal.
// It is composed of a Button, Tooltip, and the redux actions that handle opening the InviteMembersModal.
// It accepts analytics attributes passed for event 'trackAddMembersList'
export const InviteMembersControl = ({ buttonProps = {}, analyticsProps, modalProps }: InviteMembersControlProps) => {
  const {
    cta = 'Invite members',
    variant = 'primary',
    tooltipContent,
    onPress,
    isDisabled,
    isLink,
    ...restBtnProps
  } = buttonProps;
  const { componentName, type } = analyticsProps;
  const { additionalShowModalCheck, showModalOnRender = false, ...restModalProps } = modalProps;

  const suggestInvitesEnabled = enableSuggestInvitesContext();
  const { canInviteMembers, renderRequestSeats, shouldDisableButton, disabledReasons } = useInviteMembersOption();
  const { openModal: openInviteMembersModal } = useInviteMembersModal();
  const { openModal: openSuggestInvitesModal } = useSuggestInviteMembersModal();

  const disableButton = isDisabled || shouldDisableButton;
  const openModal = () => {
    if (suggestInvitesEnabled && !canInviteMembers) {
      openSuggestInvitesModal(restModalProps);
      return;
    }

    openInviteMembersModal(restModalProps);
  };

  useEffect(() => {
    // undefined is a valid value here because it's optional, so need to explicitly check for false
    if (additionalShowModalCheck === false) {
      return;
    }

    if (showModalOnRender) {
      openModal();
    }
  }, [additionalShowModalCheck]);

  if (renderRequestSeats) {
    return (
      <RequestSeatsControl
        analyticsProps={{ componentName: 'InviteMembersControl' }}
        buttonProps={{ variant: 'primary', ...buttonProps }}
      />
    );
  }

  const handleBtnClicked = (e: PressEvent) => {
    if (additionalShowModalCheck === false) {
      return;
    }

    openModal();
    trackAddMembersList({
      url: window.location.pathname,
      component: componentName,
      type,
      hasInviteMemberAccess: canInviteMembers,
    });

    onPress?.(e);
  };

  const renderButton = () => {
    if (disableButton) {
      // must return a button to use with perceivable
      return (
        <Button variant={variant} onPress={handleBtnClicked} {...restBtnProps}>
          {cta}
        </Button>
      );
    }

    if ('href' in restBtnProps) {
      return (
        <LinkButton variant={variant} onPress={handleBtnClicked} {...restBtnProps}>
          {cta}
        </LinkButton>
      );
    }

    if (isLink) {
      return (
        <Link onPress={handleBtnClicked} {...restBtnProps}>
          {cta}
        </Link>
      );
    }

    return (
      <Button variant={variant} onPress={handleBtnClicked} {...restBtnProps}>
        {cta}
      </Button>
    );
  };

  // before suggest invites was conceptualized, we only showed the invite members control if the user could invite members
  if (!suggestInvitesEnabled && !canInviteMembers) {
    return null;
  }

  if (disableButton) {
    return (
      <Perceivable>
        <TooltipTrigger>
          {renderButton()}
          {disabledReasons.length && (
            <Tooltip>
              <ul>
                {disabledReasons.map((reason, idx) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
            </Tooltip>
          )}
        </TooltipTrigger>
      </Perceivable>
    );
  }

  if (tooltipContent) {
    return (
      <TooltipTrigger>
        {renderButton()}
        <Tooltip>{tooltipContent}</Tooltip>
      </TooltipTrigger>
    );
  }

  return renderButton();
};
